/**
 * Hyperliquid Transfer & Withdrawal Operations
 *
 * User-signed actions for fund movement:
 * - withdraw3: Bridge USDC from HL L1 back to Arbitrum (~5 min, has fee)
 * - usdSend: Transfer USDC to another HL address on-chain
 * - usdClassTransfer: Move funds between Spot ↔ Perp accounts
 *
 * CRITICAL: These use `signUserSignedAction` (NOT `signL1Action`).
 * The main wallet must sign these — agent/API wallets cannot move funds.
 */

import {
  signUserSignedAction,
  userSignedActionEip712Types,
  actionSorter,
} from "@nktkas/hyperliquid/signing";
import type { AbstractWallet, Signature } from "@nktkas/hyperliquid/signing";
import { HL_MAINNET_API, HL_TESTNET_API } from "./constants";
import type { HLSignature } from "./types";

// ============================================================================
// Types
// ============================================================================

export interface HLWithdrawParams {
  /** Destination address on Arbitrum (42-char hex) */
  destination: `0x${string}`;
  /** Amount in USD (string, e.g. "100.5") */
  amount: string;
}

export interface HLUsdSendParams {
  /** Destination HL address (42-char hex) */
  destination: `0x${string}`;
  /** Amount in USD (string, e.g. "100.5") */
  amount: string;
}

export interface HLUsdClassTransferParams {
  /** Amount in USD (string, e.g. "100.5") */
  amount: string;
  /** true = Spot → Perp, false = Perp → Spot */
  toPerp: boolean;
}

export interface HLTransferResult {
  success: boolean;
  error?: string;
}

// ============================================================================
// Signature Chain ID
// ============================================================================

/** Arbitrum chain ID in hex, used in signatureChainId for user-signed actions */
const SIGNATURE_CHAIN_ID = "0xa4b1" as `0x${string}`;

// ============================================================================
// withdraw3 — Bridge USDC to Arbitrum L1
// ============================================================================

/**
 * Build the withdraw3 action object for signing and submission.
 */
export function buildWithdraw3Action(
  params: HLWithdrawParams,
  isMainnet: boolean,
) {
  const time = Date.now();
  return actionSorter.withdraw3({
    type: "withdraw3",
    signatureChainId: SIGNATURE_CHAIN_ID,
    hyperliquidChain: isMainnet ? "Mainnet" : "Testnet",
    destination: params.destination.toLowerCase() as `0x${string}`,
    amount: params.amount,
    time,
  });
}

/**
 * Sign and submit a withdraw3 action.
 *
 * @param wallet - Main wallet (NOT agent wallet). Can be a viem PrivateKeyAccount or hex private key.
 * @param params - Withdrawal parameters
 * @param isMainnet - Whether to submit to mainnet or testnet
 */
export async function submitWithdraw3(
  wallet: AbstractWallet,
  params: HLWithdrawParams,
  isMainnet = true,
): Promise<HLTransferResult> {
  const action = buildWithdraw3Action(params, isMainnet);

  const signature = await signUserSignedAction({
    wallet,
    action,
    types: userSignedActionEip712Types.withdraw3,
  });

  return postTransferAction(action, action.time, signature, isMainnet);
}

/**
 * Build EIP-712 typed data for withdraw3.
 * Use this when the wallet signing must happen in the browser (e.g., MetaMask popup).
 */
export function getWithdraw3TypedData(
  params: HLWithdrawParams,
  isMainnet: boolean,
) {
  const action = buildWithdraw3Action(params, isMainnet);
  return {
    action,
    types: userSignedActionEip712Types.withdraw3,
  };
}

// ============================================================================
// usdSend — Transfer USDC to another HL user
// ============================================================================

/**
 * Build the usdSend action object for signing and submission.
 */
export function buildUsdSendAction(
  params: HLUsdSendParams,
  isMainnet: boolean,
) {
  const time = Date.now();
  return actionSorter.usdSend({
    type: "usdSend",
    signatureChainId: SIGNATURE_CHAIN_ID,
    hyperliquidChain: isMainnet ? "Mainnet" : "Testnet",
    destination: params.destination.toLowerCase() as `0x${string}`,
    amount: params.amount,
    time,
  });
}

/**
 * Sign and submit a usdSend action.
 */
export async function submitUsdSend(
  wallet: AbstractWallet,
  params: HLUsdSendParams,
  isMainnet = true,
): Promise<HLTransferResult> {
  const action = buildUsdSendAction(params, isMainnet);

  const signature = await signUserSignedAction({
    wallet,
    action,
    types: userSignedActionEip712Types.usdSend,
  });

  return postTransferAction(action, action.time, signature, isMainnet);
}

// ============================================================================
// usdClassTransfer — Spot ↔ Perp balance transfer
// ============================================================================

/**
 * Build the usdClassTransfer action object for signing and submission.
 */
export function buildUsdClassTransferAction(
  params: HLUsdClassTransferParams,
  isMainnet: boolean,
) {
  const nonce = Date.now();
  return actionSorter.usdClassTransfer({
    type: "usdClassTransfer",
    signatureChainId: SIGNATURE_CHAIN_ID,
    hyperliquidChain: isMainnet ? "Mainnet" : "Testnet",
    amount: params.amount,
    toPerp: params.toPerp,
    nonce,
  });
}

/**
 * Sign and submit a usdClassTransfer action.
 */
export async function submitUsdClassTransfer(
  wallet: AbstractWallet,
  params: HLUsdClassTransferParams,
  isMainnet = true,
): Promise<HLTransferResult> {
  const action = buildUsdClassTransferAction(params, isMainnet);

  const signature = await signUserSignedAction({
    wallet,
    action,
    types: userSignedActionEip712Types.usdClassTransfer,
  });

  return postTransferAction(action, action.nonce, signature, isMainnet);
}

// ============================================================================
// Shared submission helper
// ============================================================================

async function postTransferAction(
  action: Record<string, unknown>,
  nonce: number,
  signature: Signature,
  isMainnet: boolean,
): Promise<HLTransferResult> {
  const baseUrl = isMainnet ? HL_MAINNET_API : HL_TESTNET_API;

  // Convert SDK Signature to our HLSignature format
  const sig: HLSignature = {
    r: signature.r,
    s: signature.s,
    v: signature.v,
  };

  try {
    const response = await fetch(`${baseUrl}/exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action,
        nonce,
        signature: sig,
      }),
    });

    if (!response.ok) {
      return {
        success: false,
        error: `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    const result = (await response.json()) as {
      status?: string;
      response?: { type?: string; error?: string };
    };

    if (result.status === "ok") {
      return { success: true };
    }

    return {
      success: false,
      error: result.response?.error ?? "Transfer failed",
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Network error",
    };
  }
}
